/**
 * AssociationsManagement - Standalone Associations Management Component
 *
 * Manages time-windowed associations between entities:
 * - Clinic-Device associations
 * - Study-Clinic associations
 * - Study-Device associations
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner, Badge, Tabs, Tab, Modal, Form } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import SearchableDropdown from '@/components/common/SearchableDropdown';
import TimeWindowTimeline from '@/components/common/TimeWindowTimeline';
import type {
  Clinic,
  Study,
  Device,
  ClinicDeviceAssociation,
  StudyClinicAssociation,
  StudyDeviceAssociation,
} from '@/types/associations';

type AssociationType = 'clinic-device' | 'study-clinic' | 'study-device';

export default function AssociationsManagement() {
  // Entity state
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [studies, setStudies] = useState<Study[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  // Association state
  const [clinicDeviceAssocs, setClinicDeviceAssocs] = useState<ClinicDeviceAssociation[]>([]);
  const [studyClinicAssocs, setStudyClinicAssocs] = useState<StudyClinicAssociation[]>([]);
  const [studyDeviceAssocs, setStudyDeviceAssocs] = useState<StudyDeviceAssociation[]>([]);

  // Selection state per tab
  const [selectedAssociations, setSelectedAssociations] = useState<Record<string, number | null>>({
    'clinic-device': null,
    'study-clinic': null,
    'study-device': null,
  });

  // Active tab
  const [activeTab, setActiveTab] = useState<AssociationType>('clinic-device');

  // Refresh states
  const [isRefreshingClinics, setIsRefreshingClinics] = useState(false);
  const [isRefreshingStudies, setIsRefreshingStudies] = useState(false);
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false);

  // Modal state
  const [showAssocModal, setShowAssocModal] = useState(false);
  const [assocModalType, setAssocModalType] = useState<AssociationType | null>(null);
  const [editingAssocId, setEditingAssocId] = useState<number | null>(null);
  const [assocForm, setAssocForm] = useState({
    clinic_id: '',
    study_id: '',
    device_id: '',
    start_timestamp: '',
    end_timestamp: '',
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clinicsRes, studiesRes, devicesRes] = await Promise.all([
        apiClient.getAdminClinics(),
        apiClient.getAdminStudies(),
        apiClient.getAdminDevices(),
      ]);

      if (!clinicsRes.error && clinicsRes.data) {
        setClinics(clinicsRes.data as Clinic[]);
      }
      if (!studiesRes.error && studiesRes.data) {
        setStudies(studiesRes.data as Study[]);
      }
      if (!devicesRes.error && devicesRes.data) {
        setDevices(devicesRes.data as Device[]);
      }

      await loadAssociations();
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssociations = async () => {
    try {
      const [clinicDeviceRes, studyClinicRes, studyDeviceRes] = await Promise.all([
        apiClient.getClinicDeviceAssociations(),
        apiClient.getStudyClinicAssociations(),
        apiClient.getStudyDeviceAssociations(),
      ]);

      if (!clinicDeviceRes.error && clinicDeviceRes.data) {
        setClinicDeviceAssocs(clinicDeviceRes.data as ClinicDeviceAssociation[]);
      }
      if (!studyClinicRes.error && studyClinicRes.data) {
        setStudyClinicAssocs(studyClinicRes.data as StudyClinicAssociation[]);
      }
      if (!studyDeviceRes.error && studyDeviceRes.data) {
        setStudyDeviceAssocs(studyDeviceRes.data as StudyDeviceAssociation[]);
      }
    } catch (error) {
      console.error('Failed to load associations:', error);
    }
  };

  // Individual refresh handlers
  const handleRefreshClinics = async () => {
    setIsRefreshingClinics(true);
    try {
      const response = await apiClient.getAdminClinics();
      if (!response.error && response.data) {
        setClinics(response.data as Clinic[]);
        toast.success('Clinics refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh clinics');
    } finally {
      setIsRefreshingClinics(false);
    }
  };

  const handleRefreshStudies = async () => {
    setIsRefreshingStudies(true);
    try {
      const response = await apiClient.getAdminStudies();
      if (!response.error && response.data) {
        setStudies(response.data as Study[]);
        toast.success('Studies refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh studies');
    } finally {
      setIsRefreshingStudies(false);
    }
  };

  const handleRefreshDevices = async () => {
    setIsRefreshingDevices(true);
    try {
      const response = await apiClient.getAdminDevices();
      if (!response.error && response.data) {
        setDevices(response.data as Device[]);
        toast.success('Devices refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh devices');
    } finally {
      setIsRefreshingDevices(false);
    }
  };

  // Helper functions
  const formatTimestamp = (ts: string) => new Date(ts).toLocaleString();

  const getEntityName = (
    id: number,
    entities: Array<{ id: number; name?: string; device_id?: string; clinic_id?: string | number; study_id?: string }>
  ): string => {
    const entity = entities.find((e) => e.id === id);
    if (!entity) return `ID: ${id}`;
    const clinicId = typeof entity.clinic_id === 'number' ? entity.clinic_id.toString() : entity.clinic_id;
    return entity.name || entity.device_id || clinicId || entity.study_id || `ID: ${id}`;
  };

  const handleSelectAssociation = (type: string, id: number) => {
    setSelectedAssociations((prev) => ({
      ...prev,
      [type]: prev[type] === id ? null : id,
    }));
  };

  const openAssocModal = (type: AssociationType, editId?: number) => {
    setAssocModalType(type);
    setEditingAssocId(editId || null);

    if (editId) {
      // Find the association to edit
      let assoc: ClinicDeviceAssociation | StudyClinicAssociation | StudyDeviceAssociation | undefined;
      if (type === 'clinic-device') {
        assoc = clinicDeviceAssocs.find((a) => a.id === editId);
        if (assoc) {
          setAssocForm({
            clinic_id: (assoc as ClinicDeviceAssociation).clinic_id.toString(),
            study_id: '',
            device_id: (assoc as ClinicDeviceAssociation).device_id.toString(),
            start_timestamp: assoc.start_timestamp.slice(0, 16), // Format for datetime-local
            end_timestamp: assoc.end_timestamp.slice(0, 16),
          });
        }
      } else if (type === 'study-clinic') {
        assoc = studyClinicAssocs.find((a) => a.id === editId);
        if (assoc) {
          setAssocForm({
            clinic_id: (assoc as StudyClinicAssociation).clinic_id.toString(),
            study_id: (assoc as StudyClinicAssociation).study_id.toString(),
            device_id: '',
            start_timestamp: assoc.start_timestamp.slice(0, 16),
            end_timestamp: assoc.end_timestamp.slice(0, 16),
          });
        }
      } else if (type === 'study-device') {
        assoc = studyDeviceAssocs.find((a) => a.id === editId);
        if (assoc) {
          setAssocForm({
            clinic_id: '',
            study_id: (assoc as StudyDeviceAssociation).study_id.toString(),
            device_id: (assoc as StudyDeviceAssociation).device_id.toString(),
            start_timestamp: assoc.start_timestamp.slice(0, 16),
            end_timestamp: assoc.end_timestamp.slice(0, 16),
          });
        }
      }
    } else {
      setAssocForm({
        clinic_id: '',
        study_id: '',
        device_id: '',
        start_timestamp: '',
        end_timestamp: '',
      });
    }
    setShowAssocModal(true);
  };

  const handleCreateAssociation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assocForm.start_timestamp || !assocForm.end_timestamp) {
      toast.error('Start and end timestamps are required');
      return;
    }

    const start = new Date(assocForm.start_timestamp).toISOString();
    const end = new Date(assocForm.end_timestamp).toISOString();

    try {
      let response;
      if (assocModalType === 'clinic-device') {
        if (!assocForm.clinic_id || !assocForm.device_id) {
          toast.error('Clinic and Device are required');
          return;
        }
        response = await apiClient.createClinicDeviceAssociation({
          clinic_id: parseInt(assocForm.clinic_id, 10),
          device_id: parseInt(assocForm.device_id, 10),
          start_timestamp: start,
          end_timestamp: end,
        });
      } else if (assocModalType === 'study-clinic') {
        if (!assocForm.study_id || !assocForm.clinic_id) {
          toast.error('Study and Clinic are required');
          return;
        }
        response = await apiClient.createStudyClinicAssociation({
          study_id: parseInt(assocForm.study_id, 10),
          clinic_id: parseInt(assocForm.clinic_id, 10),
          start_timestamp: start,
          end_timestamp: end,
        });
      } else if (assocModalType === 'study-device') {
        if (!assocForm.study_id || !assocForm.device_id) {
          toast.error('Study and Device are required');
          return;
        }
        response = await apiClient.createStudyDeviceAssociation({
          study_id: parseInt(assocForm.study_id, 10),
          device_id: parseInt(assocForm.device_id, 10),
          start_timestamp: start,
          end_timestamp: end,
        });
      } else {
        return;
      }

      if (response.error) throw new Error(response.error);
      toast.success('Association created successfully');
      setShowAssocModal(false);
      await loadAssociations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create association');
    }
  };

  const handleDeleteSelectedAssociation = (type: AssociationType) => {
    const selectedId = selectedAssociations[type];
    if (!selectedId) return;

    setConfirmModal({
      show: true,
      title: 'Delete Association',
      message: 'Are you sure you want to delete this association? This action cannot be undone.',
      onConfirm: async () => {
        try {
          let response;
          if (type === 'clinic-device') {
            response = await apiClient.deleteClinicDeviceAssociation(selectedId);
          } else if (type === 'study-clinic') {
            response = await apiClient.deleteStudyClinicAssociation(selectedId);
          } else {
            response = await apiClient.deleteStudyDeviceAssociation(selectedId);
          }

          if (response.error) throw new Error(response.error);
          toast.success('Association deleted successfully');
          await loadAssociations();
          setSelectedAssociations((prev) => ({ ...prev, [type]: null }));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete association');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Timeline data for visualization
  const getTimelineWindows = (type: AssociationType) => {
    if (type === 'clinic-device') {
      return clinicDeviceAssocs.map((a) => ({
        id: a.id,
        label: `${getEntityName(a.clinic_id, clinics)} - ${getEntityName(a.device_id, devices)}`,
        start: new Date(a.start_timestamp),
        end: new Date(a.end_timestamp),
      }));
    } else if (type === 'study-clinic') {
      return studyClinicAssocs.map((a) => ({
        id: a.id,
        label: `${getEntityName(a.study_id, studies)} - ${getEntityName(a.clinic_id, clinics)}`,
        start: new Date(a.start_timestamp),
        end: new Date(a.end_timestamp),
      }));
    } else {
      return studyDeviceAssocs.map((a) => ({
        id: a.id,
        label: `${getEntityName(a.study_id, studies)} - ${getEntityName(a.device_id, devices)}`,
        start: new Date(a.start_timestamp),
        end: new Date(a.end_timestamp),
      }));
    }
  };

  // Table renderers
  const renderClinicDeviceTable = (items: ClinicDeviceAssociation[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Clinic</th>
          <th>Device</th>
          <th>Start</th>
          <th>End</th>
        </tr>
      </thead>
      <tbody>
        {items.map((assoc) => (
          <tr
            key={assoc.id}
            onClick={() => handleSelectAssociation('clinic-device', assoc.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedAssociations['clinic-device'] === assoc.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><Badge bg="primary">{getEntityName(assoc.clinic_id, clinics)}</Badge></td>
            <td><Badge bg="secondary">{getEntityName(assoc.device_id, devices)}</Badge></td>
            <td className="text-muted small">{formatTimestamp(assoc.start_timestamp)}</td>
            <td className="text-muted small">{formatTimestamp(assoc.end_timestamp)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderStudyClinicTable = (items: StudyClinicAssociation[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Study</th>
          <th>Clinic</th>
          <th>Start</th>
          <th>End</th>
        </tr>
      </thead>
      <tbody>
        {items.map((assoc) => (
          <tr
            key={assoc.id}
            onClick={() => handleSelectAssociation('study-clinic', assoc.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedAssociations['study-clinic'] === assoc.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><Badge bg="success">{getEntityName(assoc.study_id, studies)}</Badge></td>
            <td><Badge bg="primary">{getEntityName(assoc.clinic_id, clinics)}</Badge></td>
            <td className="text-muted small">{formatTimestamp(assoc.start_timestamp)}</td>
            <td className="text-muted small">{formatTimestamp(assoc.end_timestamp)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderStudyDeviceTable = (items: StudyDeviceAssociation[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Study</th>
          <th>Device</th>
          <th>Start</th>
          <th>End</th>
        </tr>
      </thead>
      <tbody>
        {items.map((assoc) => (
          <tr
            key={assoc.id}
            onClick={() => handleSelectAssociation('study-device', assoc.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedAssociations['study-device'] === assoc.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><Badge bg="success">{getEntityName(assoc.study_id, studies)}</Badge></td>
            <td><Badge bg="secondary">{getEntityName(assoc.device_id, devices)}</Badge></td>
            <td className="text-muted small">{formatTimestamp(assoc.start_timestamp)}</td>
            <td className="text-muted small">{formatTimestamp(assoc.end_timestamp)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  // Filter functions for search - typed per association type
  const clinicDeviceFilter = (assoc: ClinicDeviceAssociation, searchTerm: string): boolean => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const entity1Name = getEntityName(assoc.clinic_id, clinics).toLowerCase();
    const entity2Name = getEntityName(assoc.device_id, devices).toLowerCase();
    const startTime = formatTimestamp(assoc.start_timestamp).toLowerCase();
    const endTime = formatTimestamp(assoc.end_timestamp).toLowerCase();
    return entity1Name.includes(term) || entity2Name.includes(term) || startTime.includes(term) || endTime.includes(term);
  };

  const studyClinicFilter = (assoc: StudyClinicAssociation, searchTerm: string): boolean => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const entity1Name = getEntityName(assoc.study_id, studies).toLowerCase();
    const entity2Name = getEntityName(assoc.clinic_id, clinics).toLowerCase();
    const startTime = formatTimestamp(assoc.start_timestamp).toLowerCase();
    const endTime = formatTimestamp(assoc.end_timestamp).toLowerCase();
    return entity1Name.includes(term) || entity2Name.includes(term) || startTime.includes(term) || endTime.includes(term);
  };

  const studyDeviceFilter = (assoc: StudyDeviceAssociation, searchTerm: string): boolean => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const entity1Name = getEntityName(assoc.study_id, studies).toLowerCase();
    const entity2Name = getEntityName(assoc.device_id, devices).toLowerCase();
    const startTime = formatTimestamp(assoc.start_timestamp).toLowerCase();
    const endTime = formatTimestamp(assoc.end_timestamp).toLowerCase();
    return entity1Name.includes(term) || entity2Name.includes(term) || startTime.includes(term) || endTime.includes(term);
  };

  // Header actions for each tab
  const renderHeaderActions = (type: AssociationType) => {
    const selectedId = selectedAssociations[type];
    return (
      <>
        {selectedId !== null && (
          <>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => openAssocModal(type, selectedId)}
              className="me-2"
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => handleDeleteSelectedAssociation(type)}
              className="me-2"
            >
              Delete
            </Button>
          </>
        )}
        <Button
          size="sm"
          variant="primary"
          onClick={() => openAssocModal(type)}
        >
          Create
        </Button>
      </>
    );
  };

  // Modal title helper
  const getModalTitle = () => {
    const prefix = editingAssocId ? 'Edit' : 'Create';
    if (assocModalType === 'clinic-device') return `${prefix} Clinic-Device Association`;
    if (assocModalType === 'study-clinic') return `${prefix} Study-Clinic Association`;
    if (assocModalType === 'study-device') return `${prefix} Study-Device Association`;
    return `${prefix} Association`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading associations...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Associations Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage time-windowed associations between clinics, studies, and devices
          </p>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as AssociationType || 'clinic-device')} className="mb-4">
        <Tab eventKey="clinic-device" title={`Clinic-Device (${clinicDeviceAssocs.length})`}>
          <DataCard
            cardKey="clinic-device-assocs"
            title="Clinic-Device Associations"
            badgeCount={clinicDeviceAssocs.length}
            data={clinicDeviceAssocs}
            itemsPerPage={10}
            searchPlaceholder="Search associations..."
            filterFunction={clinicDeviceFilter}
            headerActions={renderHeaderActions('clinic-device')}
            renderTable={renderClinicDeviceTable}
            onRefresh={loadAssociations}
            maxHeight="400px"
            beforeTable={
              clinicDeviceAssocs.length > 0 && (
                <div className="mb-3">
                  <TimeWindowTimeline windows={getTimelineWindows('clinic-device')} />
                </div>
              )
            }
            emptyMessage="No clinic-device associations found."
          />
        </Tab>

        <Tab eventKey="study-clinic" title={`Study-Clinic (${studyClinicAssocs.length})`}>
          <DataCard
            cardKey="study-clinic-assocs"
            title="Study-Clinic Associations"
            badgeCount={studyClinicAssocs.length}
            data={studyClinicAssocs}
            itemsPerPage={10}
            searchPlaceholder="Search associations..."
            filterFunction={studyClinicFilter}
            headerActions={renderHeaderActions('study-clinic')}
            renderTable={renderStudyClinicTable}
            onRefresh={loadAssociations}
            maxHeight="400px"
            beforeTable={
              studyClinicAssocs.length > 0 && (
                <div className="mb-3">
                  <TimeWindowTimeline windows={getTimelineWindows('study-clinic')} />
                </div>
              )
            }
            emptyMessage="No study-clinic associations found."
          />
        </Tab>

        <Tab eventKey="study-device" title={`Study-Device (${studyDeviceAssocs.length})`}>
          <DataCard
            cardKey="study-device-assocs"
            title="Study-Device Associations"
            badgeCount={studyDeviceAssocs.length}
            data={studyDeviceAssocs}
            itemsPerPage={10}
            searchPlaceholder="Search associations..."
            filterFunction={studyDeviceFilter}
            headerActions={renderHeaderActions('study-device')}
            renderTable={renderStudyDeviceTable}
            onRefresh={loadAssociations}
            maxHeight="400px"
            beforeTable={
              studyDeviceAssocs.length > 0 && (
                <div className="mb-3">
                  <TimeWindowTimeline windows={getTimelineWindows('study-device')} />
                </div>
              )
            }
            emptyMessage="No study-device associations found."
          />
        </Tab>
      </Tabs>

      {/* Association Modal */}
      <Modal show={showAssocModal} onHide={() => setShowAssocModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{getModalTitle()}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateAssociation}>
          <Modal.Body>
            {/* Clinic selector for clinic-device and study-clinic */}
            {(assocModalType === 'clinic-device' || assocModalType === 'study-clinic') && (
              <Form.Group className="mb-3">
                <Form.Label>Clinic <span className="text-danger">*</span></Form.Label>
                <SearchableDropdown
                  options={clinics}
                  value={assocForm.clinic_id}
                  onChange={(value) => setAssocForm({ ...assocForm, clinic_id: value })}
                  getOptionValue={(c) => c.id.toString()}
                  getOptionLabel={(c) => c.name || c.clinic_id || `Clinic ${c.id}`}
                  placeholder="Select clinic..."
                  onRefresh={handleRefreshClinics}
                  isRefreshing={isRefreshingClinics}
                />
              </Form.Group>
            )}

            {/* Study selector for study-clinic and study-device */}
            {(assocModalType === 'study-clinic' || assocModalType === 'study-device') && (
              <Form.Group className="mb-3">
                <Form.Label>Study <span className="text-danger">*</span></Form.Label>
                <SearchableDropdown
                  options={studies}
                  value={assocForm.study_id}
                  onChange={(value) => setAssocForm({ ...assocForm, study_id: value })}
                  getOptionValue={(s) => s.id.toString()}
                  getOptionLabel={(s) => s.name || s.study_id || `Study ${s.id}`}
                  placeholder="Select study..."
                  onRefresh={handleRefreshStudies}
                  isRefreshing={isRefreshingStudies}
                />
              </Form.Group>
            )}

            {/* Device selector for clinic-device and study-device */}
            {(assocModalType === 'clinic-device' || assocModalType === 'study-device') && (
              <Form.Group className="mb-3">
                <Form.Label>Device <span className="text-danger">*</span></Form.Label>
                <SearchableDropdown
                  options={devices}
                  value={assocForm.device_id}
                  onChange={(value) => setAssocForm({ ...assocForm, device_id: value })}
                  getOptionValue={(d) => d.id.toString()}
                  getOptionLabel={(d) => d.name || d.device_id || `Device ${d.id}`}
                  placeholder="Select device..."
                  onRefresh={handleRefreshDevices}
                  isRefreshing={isRefreshingDevices}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Start Time <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="datetime-local"
                value={assocForm.start_timestamp}
                onChange={(e) => setAssocForm({ ...assocForm, start_timestamp: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>End Time <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="datetime-local"
                value={assocForm.end_timestamp}
                onChange={(e) => setAssocForm({ ...assocForm, end_timestamp: e.target.value })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAssocModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingAssocId ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
